import type { IQueueStorage, QueuedEventRecord } from './types';

const DB_VERSION = 1;
const STORE_NAME = 'queue_records';

export class IndexedDBQueueStorage implements IQueueStorage {
  private readonly dbName: string;

  private db: IDBDatabase | null = null;

  constructor(dbName: string) {
    this.dbName = dbName;
  }

  async init(): Promise<void> {
    if (typeof indexedDB === 'undefined') return;

    this.db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, DB_VERSION);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error('IndexedDB open failed'));
    });
  }

  destroy(): void {
    this.db?.close();
    this.db = null;
  }

  async load(): Promise<readonly QueuedEventRecord[]> {
    if (!this.db) return [];

    return new Promise<readonly QueuedEventRecord[]>((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const records = request.result as QueuedEventRecord[];
        resolve(Array.isArray(records) ? records : []);
      };
      request.onerror = () =>
        reject(request.error ?? new Error('IndexedDB load failed'));
    });
  }

  async save(records: readonly QueuedEventRecord[]): Promise<void> {
    if (!this.db) return;

    await new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        for (const record of records) {
          store.put(record);
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error('IndexedDB save failed'));
    });
  }

  getAdapterName(): string {
    return 'IndexedDBQueueStorage';
  }

  isAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
  }
}
